import TotalSalesSvg from "@icons/total-sales.svg?react";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import intersectionWith from "lodash-es/intersectionWith";
import { DateTime } from "luxon";
import React, { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  createSearchParams,
  generatePath,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import invariant from "tiny-invariant";
import { z } from "zod";

import { ContractItem } from "@/common/components/contract-item/contract-item";
import { Failed, NoData } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { OrderItem } from "@/common/components/order-item/order-item";
import { PageTitle } from "@/common/components/page-title/page-title";
import {
  itemsPerPage,
  Pagination,
} from "@/common/components/pagination/pagination";
import { QuickFilters } from "@/common/components/quick-filters/quick-filters";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useLanguage } from "@/common/hooks/use-language";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { toggleItem } from "@/common/utils/array";

import { TwoColumnLayout } from "../layout/layout";
import { contractsQuery, totalSalesQuery } from "./contracts.api";
import { cropsInOrdersQuery, orderListQuery } from "./orders.api";
import { path as contractPath } from "./transactions-and-orders.contract";
import { path as orderPath } from "./transactions-and-orders.order";

const useIsDetailOpen = () => {
  const params = useParams();

  return !!(params.orderId ?? params.contractId);
};

const useIsCurrentContract = () => {
  const params = useParams();

  return (contractId: string) => params.contractId === contractId;
};

const useIsCurrentOrder = () => {
  const params = useParams();

  return (orderId: string) => params.orderId === orderId;
};

const path = "transactions-and-orders";
const TransactionsAndOrders = () => {
  const { _ } = useLingui();
  const isDetailOpen = useIsDetailOpen();
  const sidebar = useSidebar();

  return (
    <TwoColumnLayout
      left={{
        content: (
          <div className="flex flex-col gap-y-8">
            <Metadata title={_(msg`Transakce a příkazy`)} />
            <Overview />
            <div
              className={`grid grid-cols-1 gap-6 ${sidebar.state.screen || isDetailOpen ? "xl:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-2"} `}
            >
              <ErrorBoundary
                fallbackRender={({ error }) => <Failed error={error} />}
              >
                <Suspense fallback={<Spinner />}>
                  <ContractList />
                </Suspense>
              </ErrorBoundary>
              <ErrorBoundary
                fallbackRender={({ error }) => <Failed error={error} />}
              >
                <Suspense fallback={<Spinner />}>
                  <OrderList />
                </Suspense>
              </ErrorBoundary>
            </div>
          </div>
        ),
        header: (
          <PageTitle>
            <Trans>Transakce a příkazy</Trans>
          </PageTitle>
        ),
      }}
      right={isDetailOpen}
    />
  );
};

const ContractList = () => {
  const { id: companyId } = useRequiredErpCompany();
  const isCurrentContract = useIsCurrentContract();
  const { contractPage, setContractPage } = usePageParams();
  const locale = useLanguage();
  const contracts = useSuspenseQuery(contractsQuery({ companyId, locale }));

  const allContracts = contracts.data || [];
  const start = (contractPage - 1) * 10;
  const end = start + 10;
  const paginatedContracts = allContracts.slice(start, end);

  return (
    <div className="flex flex-col gap-y-6">
      <h2 className="text-can-forest-teal font-bold">
        <Trans>Transakce</Trans>
      </h2>
      <ul className="flex flex-col gap-y-2 md:mt-12">
        {paginatedContracts.length === 0 ? (
          <NoData>
            <Trans>Neevidujeme žádné transakce.</Trans>
          </NoData>
        ) : null}
        {paginatedContracts.map((contract) => (
          <ContractItem
            {...contract}
            className={
              isCurrentContract(contract.id) ? "bg-can-silver-gray!" : ""
            }
            externalNumber={
              contract.confirmation?.completed ? (
                <ContractItem.ExternalNumber>
                  {contract.externalNumber}
                </ContractItem.ExternalNumber>
              ) : null
            }
            key={contract.id}
            status={<ContractItem.Status status={contract.status} />}
          >
            <SearchParamsLink
              to={generatePath(contractPath, {
                contractId: contract.id,
              })}
            ></SearchParamsLink>
            <p className="text-can-midnight-steel text-xs font-bold">
              {contract.storage.name}: {contract.crop.name}
            </p>
          </ContractItem>
        ))}
      </ul>
      <Pagination
        getter={() => contractPage}
        pageSize={10}
        setter={setContractPage}
        total={allContracts.length}
      />
      <Spacer className="h-6" />
    </div>
  );
};

const OrderList = () => {
  const { id: companyId } = useRequiredErpCompany();
  const isCurrentOrder = useIsCurrentOrder();
  const locale = useLanguage();
  const crops = useSuspenseQuery(cropsInOrdersQuery({ companyId, locale }));
  const { orderFilter, setOrderFilter, orderPage, setOrderPage } =
    usePageParams();
  const orders = useSuspenseQuery(
    orderListQuery({
      companyId,
      cropIds: orderFilter,
      locale,
      page: orderPage,
    }),
  );

  const filters = crops.data.map((crop) => ({
    label: crop.name,
    value: crop.id,
  }));
  const activeFilters = intersectionWith(
    filters,
    orderFilter,
    (a, b) => a.value === b,
  );
  return (
    <div className={`flex flex-col gap-y-6`}>
      <h2 className="text-can-forest-teal font-bold">
        <Trans>Aktivní příkazy</Trans>
      </h2>
      <div className="flex max-w-[calc(100vw-(--spacing(12)))] gap-3 overflow-x-auto">
        <SearchParamsLink
          className="border-can-silver-cloud text-can-forest-teal border-r pr-2"
          omit={["order_filter"]}
          replace
          to="."
        >
          <Trans context="meaning no filters are being applied">Vše</Trans>
        </SearchParamsLink>
        <QuickFilters
          activeFilter={activeFilters}
          filters={filters}
          onClick={(i) => setOrderFilter(filters[i].value)}
        />
      </div>
      <ul className="flex flex-col gap-y-2">
        {orders.data.orders.length === 0 ? (
          <NoData>
            <Trans>Neevidujeme žádné aktivní příkazy.</Trans>
          </NoData>
        ) : (
          orders.data.orders.map((order) => (
            <OrderItem
              {...order.order}
              className={
                isCurrentOrder(order.order.id) ? "bg-can-silver-gray!" : ""
              }
              key={order.order.id}
            >
              <SearchParamsLink
                to={generatePath(orderPath, {
                  offerId: order.offer.id,
                  orderId: order.order.id,
                })}
              ></SearchParamsLink>
              <p className="text-can-midnight-steel text-xs font-bold">
                {order.storage.name}: {order.crop.name}
              </p>
            </OrderItem>
          ))
        )}
      </ul>
      <Pagination
        getter={() => orderPage}
        pageSize={itemsPerPage.ten}
        setter={setOrderPage}
        total={orders.data.total}
      />
      <Spacer className="h-6" />
    </div>
  );
};

const Params = ({ children }: React.PropsWithChildren) => {
  const { params } = useTransactionsAndOrdersParams();
  const { pathname } = useLocation();

  if (!params) {
    return <Navigate replace to={`${pathname}?${initialParams}`} />;
  }

  return <>{children}</>;
};

const ranges = [
  "today",
  "last_week",
  "last_month",
  "three_months",
  "last_year",
  "all",
] as const;
const filters = () =>
  [
    {
      dateFrom: DateTime.now().startOf("day"),
      dateTo: DateTime.now().startOf("day"),
      label: t`Dnes`,
      value: "today",
    },
    {
      dateFrom: DateTime.now().startOf("day").minus({ week: 1 }),
      dateTo: DateTime.now().startOf("day"),
      label: t`Týden`,
      value: "last_week",
    },
    {
      dateFrom: DateTime.now().startOf("day").minus({ month: 1 }),
      dateTo: DateTime.now().startOf("day"),
      label: t`Měsíc`,
      value: "last_month",
    },
    {
      dateFrom: DateTime.now().startOf("day").minus({ month: 3 }),
      dateTo: DateTime.now().startOf("day"),
      label: t`3 měsíce`,
      value: "three_months",
    },
    {
      dateFrom: DateTime.now().startOf("day").minus({ year: 1 }),
      dateTo: DateTime.now().startOf("day"),
      label: t`Rok`,
      value: "last_year",
    },
    {
      label: t`Vše`,
      value: "all",
    },
  ] as const satisfies {
    label: string;
    value: Range;
    dateFrom?: DateTime;
    dateTo?: DateTime;
  }[];
type Range = (typeof ranges)[number];
const Overview = () => {
  const { overviewRange, setOverviewRange } = usePageParams();
  const isDetailOpen = useIsDetailOpen();
  const activeFilter = filters().find(({ value }) => value === overviewRange);

  return (
    <div className="flex max-w-[calc(100vw-(--spacing(12)))] flex-col gap-y-6">
      <QuickFilters
        activeFilter={activeFilter}
        filters={filters()}
        onClick={(i) => {
          setOverviewRange(filters()[i].value);
        }}
      />
      <ul
        className={`${isDetailOpen ? "pr-6" : ""} flex flex-col gap-y-2 sm:flex-row sm:justify-between sm:gap-6`}
      >
        <li className="shadow-can-light-box flex min-h-[84px] w-full max-w-[272px] items-center justify-between rounded-2xl p-4">
          <ErrorBoundary
            fallbackRender={({ error }) => <Failed error={error} />}
          >
            <Suspense fallback={<Spinner withHint={false} />}>
              <TotalSales />
            </Suspense>
          </ErrorBoundary>
        </li>
      </ul>
    </div>
  );
};

const TotalSales = () => {
  const { id: companyId } = useRequiredErpCompany();
  const { overviewRange } = usePageParams();
  const activeFilter = filters().find(({ value }) => value === overviewRange);
  invariant(activeFilter);
  const locale = useLanguage();
  const sum = useSuspenseQuery(
    totalSalesQuery({
      companyId,
      dateFrom:
        activeFilter.value === "all" ? undefined : activeFilter.dateFrom,
      dateTo: activeFilter.value === "all" ? undefined : activeFilter.dateTo,
      locale,
    }),
  );

  return (
    <>
      <div className="bg-can-silver-gray flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full">
        <TotalSalesSvg />
      </div>
      <div className="flex flex-col gap-y-2 text-right text-xs">
        <h4>
          <Trans>Celkový objem prodejů</Trans>
        </h4>
        <span className="text-can-midnight-steel text-xl font-bold">
          {sum.data}
        </span>
      </div>
    </>
  );
};

const initialParams = createSearchParams({
  contract_page: "1",
  order_filter: "[]",
  order_page: "1",
  overview_range: "three_months",
});

const paramsSchema = z.object({
  contract_page: z.coerce.number().min(1),
  order_filter: z.string(),
  order_page: z.coerce.number().min(1),
  overview_range: z.enum(ranges),
});

const useTransactionsAndOrdersParams = () => {
  const [searchParams] = useSearchParams();
  const params = paramsSchema.safeParse(Object.fromEntries(searchParams));

  if (!params.success) {
    return { params: undefined };
  }

  const orderFilterInTact = z
    .string()
    .transform((v) => {
      try {
        return JSON.parse(v);
      } catch {
        return z.never;
      }
    })
    .pipe(z.array(z.string()))
    .safeParse(params.data.order_filter);

  if (!orderFilterInTact.success) {
    return { params: undefined };
  }

  return {
    params: {
      ...params.data,
    },
  };
};

const usePageParams = () => {
  const { params } = useTransactionsAndOrdersParams();
  const safeParams = paramsSchema
    .transform((p) => ({ ...p, order_filter: JSON.parse(p.order_filter) }))
    .parse(params);
  const [, setSearchParams] = useSearchParams();

  const setOverviewRange = (range: Range) => {
    setSearchParams((c) => {
      c.set("overview_range", range);
      return c;
    });
  };

  const setContractPage = (page: number) => {
    setSearchParams((c) => {
      c.set("contract_page", page.toString());
      return c;
    });
  };

  const setOrderFilter = (cropId: string) => {
    setSearchParams((c) => {
      const filters = JSON.parse(c.get("order_filter") ?? "[]");
      const nextFilters = toggleItem(filters, cropId);

      c.set("order_filter", JSON.stringify(nextFilters));
      return c;
    });
  };

  const setOrderPage = (page: number) => {
    setSearchParams((c) => {
      c.set("order_page", page.toString());
      return c;
    });
  };

  return {
    contractPage: safeParams.contract_page,
    orderFilter: safeParams.order_filter,
    orderPage: safeParams.order_page,
    overviewRange: safeParams.overview_range,
    setContractPage,
    setOrderFilter,
    setOrderPage,
    setOverviewRange,
  };
};

export { Params, path, TransactionsAndOrders };
